import Quill from 'quill'
import '../styles/quill-spacing.css'

const Parchment = Quill.import('parchment')
const SPACING_INCREMENT = 8
const SPACING_VALUES = ['0px', '8px', '16px', '24px', '32px', '40px', '48px', '56px', '64px']
const FontAttributor = Quill.import('formats/font')

export const FONT_WHITELIST = [
  'arial',
  'timesnewroman',
  'couriernew',
  'verdana',
  'georgia',
  'trebuchetms',
  'comicsansms',
  'serif',
  'monospace'
]

export const FONT_TOOLBAR_OPTIONS = ['', ...FONT_WHITELIST]

export const FULL_TOOLBAR_WITH_FONTS = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ font: FONT_TOOLBAR_OPTIONS }],
  [{ align: [] }],
  ['link', 'video', 'image'],
  ['clean']
]
export const FULL_TOOLBAR_WITH_SPACING = [
  [{ font: FONT_TOOLBAR_OPTIONS }],
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ lineheight: [false, '1', '1.15', '1.5', '2', '2.5', '3'] }],
  ['spacingbefore', 'spacingbeforedecrease', 'spacingafter', 'spacingafterdecrease'],
  ['blockquote', 'code-block'],
  ['clean']
]

const createStyleAttributor = (name, cssProperty, whitelist) =>
  new Parchment.Attributor.Style(name, cssProperty, {
    scope: Parchment.Scope.BLOCK,
    whitelist
  })

export const LineHeight = createStyleAttributor(
  'lineheight',
  'line-height',
  [
    '1',
    '1.15',
    '1.5',
    '2',
    '2.5',
    '3'
  ]
)

export const SpacingBefore = createStyleAttributor(
  'spacingbefore',
  'margin-top',
  SPACING_VALUES
)

export const SpacingAfter = createStyleAttributor(
  'spacingafter',
  'margin-bottom',
  SPACING_VALUES
)

FontAttributor.whitelist = FONT_WHITELIST
Quill.register(FontAttributor, true)
Quill.register(LineHeight, true)
Quill.register(SpacingBefore, true)
Quill.register(SpacingAfter, true)

const Embed = Quill.import('blots/embed')

class SoftBreak extends Embed {}
SoftBreak.blotName = 'softbreak'
SoftBreak.tagName = 'BR'
Quill.register(SoftBreak)

const Keyboard = Quill.import('modules/keyboard')
Keyboard.DEFAULTS.bindings.softbreak = {
  key: 13,
  shiftKey: true,
  handler(range) {
    const nextLeaf = this.quill.getLeaf(range.index + 1)[0]
    this.quill.insertEmbed(range.index, 'softbreak', true, Quill.sources.USER)
    if (nextLeaf == null) {
      this.quill.insertEmbed(range.index, 'softbreak', true, Quill.sources.USER)
    }
    this.quill.setSelection(range.index + 1, Quill.sources.SILENT)
  }
}

const incrementSpacing = (quill, format) => {
  const range = quill.getSelection()
  if (!range) return

  const currentValue = quill.getFormat(range)[format]
  const currentSpacing = Number.parseInt(currentValue, 10) || 0
  const maxSpacing = Number.parseInt(SPACING_VALUES[SPACING_VALUES.length - 1], 10)
  const nextSpacing = Math.min(currentSpacing + SPACING_INCREMENT, maxSpacing)

  quill.format(format, `${nextSpacing}px`)
}

const decrementSpacing = (quill, format) => {
  const range = quill.getSelection()
  if (!range) return

  const currentValue = quill.getFormat(range)[format]
  const currentSpacing = Number.parseInt(currentValue, 10) || 0
  const nextSpacing = Math.max(currentSpacing - SPACING_INCREMENT, 0)

  quill.format(format, nextSpacing === 0 ? false : `${nextSpacing}px`)
}

const spacingToolbarHandlers = {
  spacingbefore() {
    incrementSpacing(this.quill, 'spacingbefore')
  },
  spacingafter() {
    incrementSpacing(this.quill, 'spacingafter')
  },
  spacingbeforedecrease() {
    decrementSpacing(this.quill, 'spacingbefore')
  },
  spacingafterdecrease() {
    decrementSpacing(this.quill, 'spacingafter')
  }
}

export const createSpacingToolbarConfig = (container) => ({
  container,
  handlers: spacingToolbarHandlers
})
